/**
 * What the hero's rocket layer is allowed to fly.
 *
 * The scene above the hero is decoration, but the cards it launches are not.
 * Every one of them is a listing that is actually on the board, at the figure
 * its company has actually paid — the same rule the Live Board keeps, applied
 * to the air above it. Nothing here invents a company, a role, or a number, and
 * an empty board produces an empty sky rather than a demonstration of one.
 *
 * The ambient loop deliberately flies the rows the Live Board is *not* showing:
 * the board holds the top four, so the rockets start at the fifth. A visitor
 * therefore sees more of the real board than the four rows in front of them,
 * not the same four twice. Only when the board is shorter than that — a young
 * board with four listings or fewer — do the rockets fall back to the rows on
 * display, because a real row shown twice is still true and an invented one
 * never is.
 */

import type { BidEvent, RankedEntry } from '@bidtohire/shared';

import { BOARD_SIZE } from './live-board';
import {
  compactCompensationLabel,
  formatMoneyCents,
  initials,
  markColour,
} from './format';

/**
 * How many distinct rows the ambient loop keeps in rotation.
 *
 * Each one costs a canvas texture per depth tier the first time it flies, so
 * the cap is a memory ceiling as much as an editorial one. Twelve is more than
 * a visitor can track and far less than a full page of the board.
 */
export const ROCKET_ROW_LIMIT = 12;

/** One job row, reduced to exactly what the card texture draws. */
export interface RocketRow {
  /** Stable per listing: the texture cache is keyed on it. */
  readonly key: string;
  readonly title: string;
  readonly company: string;
  /** The company's initials, matching the mark the board draws. */
  readonly mark: string;
  /** The mark's colour, matching the board's for the same company. */
  readonly markColour: string;
  /** "Company · Location", or just the company when the location is unstated. */
  readonly meta: string;
  /** A compacted salary — "$185k – $225k" — or undefined when undisclosed. */
  readonly salary?: string;
  /** The lifetime total paid, formatted. This is the listing's rank score. */
  readonly paid: string;
}

function toRow(input: {
  readonly key: string;
  readonly title: string;
  readonly companyName: string;
  readonly companySlug: string;
  readonly locationLabel?: string;
  readonly compensationLabel?: string;
  readonly totalCents: number;
}): RocketRow {
  const location = input.locationLabel;
  return {
    key: input.key,
    title: input.title,
    company: input.companyName,
    mark: initials(input.companyName),
    markColour: markColour(input.companySlug),
    meta:
      location === undefined || location === ''
        ? input.companyName
        : `${input.companyName} · ${location}`,
    salary: compactCompensationLabel(input.compensationLabel),
    paid: formatMoneyCents(input.totalCents),
  };
}

/**
 * The ambient rotation, drawn from a board read.
 *
 * Returns an empty list for an empty board. That is the whole failure mode:
 * there is no placeholder listing, so a board with nothing on it flies nothing.
 */
export function rocketRowsFromEntries(
  entries: readonly RankedEntry[],
  boardSize: number = BOARD_SIZE,
  limit: number = ROCKET_ROW_LIMIT,
): readonly RocketRow[] {
  if (entries.length === 0) return [];
  const beyondTheBoard = entries.slice(boardSize);
  const pool = beyondTheBoard.length > 0 ? beyondTheBoard : entries;
  return pool.slice(0, limit).map((entry) =>
    toRow({
      key: entry.jobId,
      title: entry.title,
      companyName: entry.company.name,
      companySlug: entry.company.slug,
      locationLabel: entry.locationLabel,
      compensationLabel: entry.compensationLabel,
      totalCents: entry.totalPaidCents,
    }),
  );
}

/**
 * The row a cleared bid earns: the listing that was just paid for, at the total
 * the payment left it on. The event carries every field the card draws, so this
 * needs no second read and cannot show a figure the payment disagrees with.
 */
export function rocketRowFromBid(event: BidEvent): RocketRow {
  return toRow({
    key: event.jobId,
    title: event.title,
    companyName: event.companyName,
    companySlug: event.companySlug,
    locationLabel: event.locationLabel,
    compensationLabel: event.compensationLabel,
    totalCents: event.totalCents,
  });
}
